import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { IncidentConfig } from './homeConfig';

/**
 * Subset of the announcements plugin's `Announcement` type
 * (`@backstage-community/plugin-announcements-common` 0.8). Read through the
 * announcements backend REST API so the app does not need extra dependencies.
 */
export interface HomeAnnouncement {
  id: string;
  title: string;
  excerpt: string;
  created_at: string;
  start_at?: string;
  active: boolean;
  category?: { slug: string; title: string };
}

interface AnnouncementsList {
  count: number;
  results: HomeAnnouncement[];
}

export interface AnnouncementsQuery {
  category?: string;
  max?: number;
  active?: boolean;
  sortBy?: 'created_at' | 'start_at';
}

export const ANNOUNCEMENTS_PATH = '/announcements';
export const announcementViewPath = (id: string) => `${ANNOUNCEMENTS_PATH}/view/${encodeURIComponent(id)}`;
export const announcementCategoryPath = (slug: string) =>
  `${ANNOUNCEMENTS_PATH}?category=${encodeURIComponent(slug)}`;

export async function fetchAnnouncements(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  query: AnnouncementsQuery,
): Promise<HomeAnnouncement[]> {
  const params = new URLSearchParams();
  if (query.category) params.set('category', query.category);
  if (query.max) params.set('max', String(query.max));
  if (query.active) params.set('active', 'true');
  if (query.sortBy) params.set('sortby', query.sortBy);
  params.set('order', 'desc');

  const baseUrl = await discoveryApi.getBaseUrl('announcements');
  const response = await fetchApi.fetch(`${baseUrl}/announcements?${params.toString()}`);
  if (!response.ok) {
    throw new Error(`Failed to load announcements (HTTP ${response.status})`);
  }
  const body = (await response.json()) as AnnouncementsList;
  return body.results ?? [];
}

export type IncidentKind = 'outage' | 'maintenance';

export interface Incident {
  kind: IncidentKind;
  upcoming: boolean;
  announcement: HomeAnnouncement;
}

/**
 * The announcements plugin (0.11) has a start date but no end date, so an
 * Outage/Maintenance announcement is treated as current while it is active
 * and started less than `windowHours` ago. Maintenance starting within
 * `upcomingHours` is also surfaced as "scheduled".
 */
export function toIncident(
  announcement: HomeAnnouncement,
  config: IncidentConfig,
  now: number = Date.now(),
): Incident | undefined {
  const slug = announcement.category?.slug;
  let kind: IncidentKind | undefined;
  if (slug === config.outageCategory) kind = 'outage';
  else if (slug === config.maintenanceCategory) kind = 'maintenance';
  if (!kind || !announcement.active) return undefined;

  const start = Date.parse(announcement.start_at || announcement.created_at);
  if (Number.isNaN(start)) return undefined;
  const hour = 3600 * 1000;
  if (start > now) {
    return kind === 'maintenance' && start - now <= config.upcomingHours * hour
      ? { kind, upcoming: true, announcement }
      : undefined;
  }
  return now - start <= config.windowHours * hour ? { kind, upcoming: false, announcement } : undefined;
}

export async function fetchIncidents(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  config: IncidentConfig,
): Promise<Incident[]> {
  const [outages, maintenance] = await Promise.all(
    [config.outageCategory, config.maintenanceCategory].map(category =>
      fetchAnnouncements(discoveryApi, fetchApi, { category, active: true, max: 10, sortBy: 'start_at' }),
    ),
  );
  const now = Date.now();
  return [...outages, ...maintenance]
    .map(announcement => toIncident(announcement, config, now))
    .filter((incident): incident is Incident => Boolean(incident));
}
